import Column from '@app/components/layout/Column'
import { Breadcrumbs } from '@app/components/core/breadcrumbs/Breadcrumbs'
import { InitiativeViewPageLayout } from '@app/pages/layout/InitiativeViewPageLayout'
import { useEffect, useState } from 'react'
import { useClient, useAuthState } from '@based/sdk'
import { useTranslations } from '@i18n/useLanguage'
import { useLocation } from 'wouter'
import { pages } from '@app/util/links'
import { initiativeCreationSteps } from '../../shared/workflow'
import { PHASE_TYPES, PhaseType } from '@schema/phase'
import { LivingParametersEdit } from './edit/LivingParametersEdit'
import { MembersParametersEdit } from './edit/MembersParametersEdit'
import { MainDetailsEdit } from './edit/MainDetailsEdit'
import { LegalInfoEdit } from './edit/LegalInfoEdit'
import { AmenitiesEdit } from './edit/AmenitiesEdit'
import { ContactDetailsEdit } from './edit/ContactDetailsEdit'
import { PageContentEdit } from './edit/PageContentEdit'
import { Button } from '@app/components/core/button/Button'
import { HorizontalSeparator } from '@app/components/core/separator/HorizontalSeparator'
import { InitiativeWithId } from '../../shared/initiativeTypes'

export const InitiativeAbout = ({ initiativeId }: { initiativeId: string }) => {
  const client = useClient()
  const authState = useAuthState()
  const [_, navigate] = useLocation()
  const translationsRoot = useTranslations().app
  const translations = translationsRoot.dashboard.initiative
  const [initiative, setInitiative] = useState<InitiativeWithId | null>(null)
  useEffect(() => {
    client
      .query('get-initiative', {
        id: Number(initiativeId),
        userId: authState.userId,
      })
      .subscribe(
        (result) => {
          setInitiative(result)
        },
        () => navigate(pages.dashboardInitiatives),
      )
  }, [client, initiativeId, authState, setInitiative, navigate])

  if (!initiative) {
    return (
      <InitiativeViewPageLayout>
        <Column alignItems="stretch" gap="1.5rem">
          <Breadcrumbs
            levels={[
              {
                label: translations.common.myInitiatives,
                to: pages.dashboardInitiatives,
              },
              {
                label: translations.common.loading,
              },
            ]}
          />
        </Column>
      </InitiativeViewPageLayout>
    )
  }

  const title =
    initiative.title.trim() !== ''
      ? initiative.title
      : translations.common.unnamedInitiative

  const isOnboardingComplete =
    initiative.isCalendlyScheduled || initiative.isLive

  const phaseType: PhaseType =
    initiative.phase?.phaseType ?? PHASE_TYPES.idea
  const showLegalInfo =
    phaseType !== PHASE_TYPES.idea && phaseType !== PHASE_TYPES.living
  const showAmenities = phaseType === PHASE_TYPES.living

  return (
    <InitiativeViewPageLayout>
      <Column alignItems="stretch" gap="1.5rem" style={{ flexGrow: 0 }}>
        <Breadcrumbs
          levels={[
            {
              label: translations.common.myInitiatives,
              to: pages.dashboardInitiatives,
            },
            {
              label: title,
            },
          ]}
        />
      </Column>
      <Column gap="2rem" alignItems="stretch" style={{ flexGrow: 0 }}>
        <MainDetailsEdit initiative={initiative} />
        {isOnboardingComplete ? (
          <>
            <HorizontalSeparator />
            <PageContentEdit initiative={initiative} />
            <HorizontalSeparator />
            <LivingParametersEdit initiative={initiative} />
            <HorizontalSeparator />
            <MembersParametersEdit initiative={initiative} />

            {showLegalInfo && (
              <>
                <HorizontalSeparator />
                <LegalInfoEdit initiative={initiative} />
              </>
            )}
            {showAmenities && (
              <>
                <HorizontalSeparator />
                <AmenitiesEdit initiative={initiative} />
              </>
            )}
            <HorizontalSeparator />
            <ContactDetailsEdit initiative={initiative} />
          </>
        ) : (
          <Button
            label={translations.view.finishOnboarding}
            onClick={() =>
              navigate(
                pages.dashboardOnboardInitiative(
                  initiative.id.toString(),
                  initiative.onboardingStep ?? initiativeCreationSteps.livingVision,
                ),
              )
            }
          />
        )}
      </Column>
    </InitiativeViewPageLayout>
  )
}
